import React, { useState, useEffect } from 'react';
import { MdPhone, MdSearch, MdPerson, MdChatBubbleOutline } from 'react-icons/md';
import axiosInstance from '../../services/axiosInstance';
import { hooks } from '../../hooks';
import { theme } from '../../constants';
import { components } from '../../components';
import { useLanguage } from '../../context/LanguageContext';
import { ChatListView } from '../../components/ChatListView';
import { text } from '../../text';
import { ensureMicrophonePermission } from '../../services/capacitorService';

const PhoneIcon = MdPhone as any;
const SearchIcon = MdSearch as any;
const PersonIcon = MdPerson as any;
const MessageIcon = MdChatBubbleOutline as any;

export const Contacts: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const authSlice = hooks.useAppSelector((state: any) => state.authSlice);
  const { t } = useLanguage();
  const tc = (t as any).contacts;
  const [users, setUsers] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [callingId, setCallingId] = useState<number | null>(null);
  const [callError, setCallError] = useState('');
  const [messagingId, setMessagingId] = useState<number | null>(null);
  // Ingat tab terakhir yang dibuka (sessionStorage) — supaya saat navigasi ke ChatRoom
  // lalu tekan back, layar ini kembali ke tab yang sama, bukan reset ke "Contacts".
  const [view, setView] = useState<'contacts' | 'chats'>(
    () => (sessionStorage.getItem('contacts_view') as 'contacts' | 'chats') || 'contacts'
  );
  useEffect(() => {
    sessionStorage.setItem('contacts_view', view);
  }, [view]);

  // Ingat posisi scroll per-tab (Contacts vs Chats) — dipulihkan saat tab ini aktif
  // lagi (baik lewat toggle maupun remount setelah balik dari ChatRoom), dan disimpan
  // setiap kali meninggalkan tab ini (ganti tab atau navigasi keluar layar).
  useEffect(() => {
    const key = `contacts_scroll_${view}`;
    const saved = sessionStorage.getItem(key);
    if (saved) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.getElementById('scrollableDiv');
          if (el) el.scrollTop = parseInt(saved, 10);
        });
      });
    }
    return () => {
      const el = document.getElementById('scrollableDiv');
      if (el) sessionStorage.setItem(key, String(el.scrollTop));
    };
  }, [view]);
  const imageBase = process.env.REACT_APP_IMAGE_BASE_URL || '';

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        const res = await axiosInstance.get('/users');
        const all = Array.isArray(res.data) ? res.data : (res.data.users || []);
        setUsers(all.filter((u: any) => u.id !== authSlice.userId));
      } catch (err) {
        console.error('Error fetching users:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, [authSlice.userId]);

  const handleCall = async (targetUser: any) => {
    if (callingId !== null) return;
    setCallError('');

    // Minta izin mikrofon lewat plugin native dulu (sama jalur dengan Camera) sebelum
    // getUserMedia — supaya RECORD_AUDIO/AVAudioSession sudah granted di level native,
    // bukan baru diminta lewat WebView.onPermissionRequest() saat getUserMedia dipanggil.
    const nativeGranted = await ensureMicrophonePermission();
    if (!nativeGranted) {
      setCallError((tc as any)?.micPermissionDenied || 'Izin mikrofon diperlukan untuk melakukan panggilan');
      return;
    }

    // getUserMedia resolves immediately if permission was already granted.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
    } catch (err: any) {
      // Cuma NotAllowedError/PermissionDeniedError yang beneran berarti izin ditolak user/OS —
      // error lain (NotFoundError, NotReadableError/mic lagi dipakai app lain, AbortError, dll)
      // BUKAN soal permission, jangan diarahkan ke "aktifkan izin manual" karena menyesatkan.
      const isPermissionError = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
      console.error('getUserMedia pre-check failed:', err?.name, err?.message);
      setCallError(
        isPermissionError
          ? ((tc as any)?.micPermissionDenied || 'Izin mikrofon diperlukan untuk melakukan panggilan')
          : ((tc as any)?.micAccessFailed || 'Gagal mengakses mikrofon. Silakan coba lagi sesaat.')
      );
      return;
    }

    try {
      setCallingId(targetUser.id);
      const res = await axiosInstance.post('/calls/initiate', { target_user_id: targetUser.id });
      const { token, room_name, livekit_url } = res.data;
      navigate('/CallScreen', {
        state: { token, room_name, livekit_url, callee_name: targetUser.name, callee_id: targetUser.id },
      });
    } catch (err: any) {
      console.error('Error initiating call:', err);
      const msg = err?.response?.data?.message || 'Gagal memulai panggilan';
      setCallError(msg);
      setCallingId(null);
    }
  };

  const handleMessage = async (targetUser: any) => {
    if (messagingId !== null) return;
    setCallError('');
    try {
      setMessagingId(targetUser.id);
      const res = await axiosInstance.post(`/chat/conversations/direct/${targetUser.id}`);
      navigate('/ChatRoom', {
        state: { conversationId: res.data.conversation_id, title: targetUser.name, type: 'direct', photo: targetUser.profile_picture || null },
      });
    } catch (err: any) {
      setCallError(err?.response?.data?.message || 'Gagal membuka chat');
    } finally {
      setMessagingId(null);
    }
  };

  const filtered = users.filter(
    (u) =>
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div id='screen'>
      <components.Header title={view === 'contacts' ? (tc?.title || 'Contacts') : (tc?.chatsTab || 'Chat')} />
      <main id="scrollableDiv" className='container' style={{ marginTop: 62, paddingBottom: 110, height: 'calc(100vh - 62px)', overflowY: 'auto' }}>
        {/* Toggle Contacts / Chats */}
        <div
          style={{
            display: 'flex', backgroundColor: '#F3F4F6', borderRadius: 10, padding: 4,
            marginTop: 2, marginBottom: 12, gap: 4,
          }}
        >
          {(['contacts', 'chats'] as const).map((v) => (
            <div
              key={v}
              onClick={() => setView(v)}
              style={{
                flex: 1, textAlign: 'center', padding: '8px 0', borderRadius: 8, cursor: 'pointer',
                backgroundColor: view === v ? '#fff' : 'transparent',
                boxShadow: view === v ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              <text.T14 style={{ color: view === v ? theme.colors.mainColor : '#6B7280', fontWeight: view === v ? '700' : '500', textAlign: 'center' }}>
                {v === 'contacts' ? (tc?.contactsTab || 'Contacts') : (tc?.chatsTab || 'Chats')}
              </text.T14>
            </div>
          ))}
        </div>

        {view === 'chats' ? (
          <ChatListView />
        ) : (
          <>
        {/* Search */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#fff',
            borderRadius: 10,
            padding: '10px 12px',
            gap: 8,
            border: '1px solid #E5E7EB',
            marginBottom: 12,
          }}
        >
          <SearchIcon size={18} color="#9CA3AF" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tc?.searchPlaceholder || 'Search users...'}
            style={{
              border: 'none',
              outline: 'none',
              fontSize: 14,
              flex: 1,
              backgroundColor: 'transparent',
              color: theme.colors.mainDark,
            }}
          />
        </div>

        {/* Error banner */}
        {callError && (
          <div
            style={{
              backgroundColor: '#FEE2E2',
              color: '#DC2626',
              padding: '8px 12px',
              borderRadius: 8,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {callError}
          </div>
        )}

        {/* User list */}
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6B7280', fontSize: 14 }}>
            {t.common.loading}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6B7280', fontSize: 14 }}>
            {tc?.noUsersFound || 'No users found'}
          </div>
        ) : (
          filtered.map((user) => (
            <div
              key={user.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#fff',
                borderRadius: 12,
                padding: '12px 14px',
                marginBottom: 10,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                border: '1px solid #F4F6F8',
                gap: 12,
              }}
            >
              {/* Avatar */}
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  backgroundColor: '#E5E7EB',
                  overflow: 'hidden',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {user.profile_picture ? (
                  <img
                    src={`${imageBase}/${user.profile_picture}`}
                    alt={user.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <PersonIcon size={24} color="#9CA3AF" />
                )}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: 14,
                    fontWeight: 600,
                    color: theme.colors.mainDark,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user.name}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    color: '#6B7280',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user.role || user.email}
                </p>
              </div>

              {/* Call button */}
              <button
                onClick={() => handleCall(user)}
                disabled={callingId !== null}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  backgroundColor: callingId === user.id ? '#FFD5C8' : theme.colors.mainColor,
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: callingId !== null ? 'default' : 'pointer',
                  flexShrink: 0,
                  opacity: callingId !== null && callingId !== user.id ? 0.4 : 1,
                }}
              >
                <PhoneIcon size={20} color="#fff" />
              </button>

              {/* Message button */}
              <button
                onClick={() => handleMessage(user)}
                disabled={messagingId !== null}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  backgroundColor: messagingId === user.id ? '#DBEAFE' : '#3B82F6',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: messagingId !== null ? 'default' : 'pointer',
                  flexShrink: 0,
                  opacity: messagingId !== null && messagingId !== user.id ? 0.4 : 1,
                }}
              >
                <MessageIcon size={19} color="#fff" />
              </button>
            </div>
          ))
        )}
          </>
        )}
      </main>
      <components.BottomTabBar />
    </div>
  );
};
